import { createFileRoute } from '@tanstack/react-router'
import React, { useEffect, useDeferredValue } from 'react'
import { DashboardTableSwitcher } from '../components/DashboardTableSwitcher'
import { LiveStrikeChangeTracker } from '../components/LiveStrikeChangeTracker'
import { DashboardRankingTable } from '../components/tables/DashboardRankingTable'
import { useDashboardData } from '../hooks/useDashboardData'
import FullPageLoader from '../components/ui/FullPageLoader'
import { usePageTitle } from '../hooks/usePageTitle'
import { useFastMount } from '../hooks/useFastMount'
import { useSettingsStore } from '../stores/settingsStore'

// Route Definition
export const Route = createFileRoute('/_auth/dashboard')({
    component: DashboardRoute,
})

function DashboardRoute() {
    usePageTitle('Dashboard')
    // FREEZE FIX v3: Granular selectors prevent re-renders from unrelated store changes
    const symbol = useSettingsStore(s => s.symbol)
    const expiry = useSettingsStore(s => s.expiry)
    const mode = useSettingsStore(s => s.mode)
    const range = useSettingsStore(s => s.range)
    const isLive = useSettingsStore(s => s.isLive)
    const setExpiry = useSettingsStore(s => s.setExpiry)

    // FREEZE FIX: Defer the parameters passed to heavy sub-components
    const deferredSymbol = useDeferredValue(symbol)
    const deferredExpiry = useDeferredValue(expiry)
    const deferredMode = useDeferredValue(mode)
    const deferredRange = useDeferredValue(range)

    // Lightweight object for prop passing
    const settings = { symbol: deferredSymbol, expiry: deferredExpiry, mode: deferredMode, range: deferredRange, isLive, setExpiry }

    // 1. Data Hooks
    const dashboardData = useDashboardData()
    const { marketData, analysis, expiries, isLoading: isMarketLoading, effectiveExpiry } = dashboardData

    // 🚀 PERF FIX: Removed useDeferredValue. It causes render starvation because the
    // 2-sec live ticks continuously interrupt the deferred render, causing React to
    // abort it and never actually paint the new data to the screen.
    const deferredMarketData = marketData
    const deferredAnalysis = analysis




    const isMounted = useFastMount()

    if (!isMounted || (isMarketLoading && !marketData)) {
        return (
            <div className="space-y-6 pb-20 p-4">
                <div className="min-h-[550px] w-full rounded-2xl bg-slate-100/50 dark:bg-slate-800/20 animate-pulse border border-slate-200/50 dark:border-slate-700/50" />
                <div className="min-h-[420px] w-full rounded-2xl bg-slate-100/50 dark:bg-slate-800/20 animate-pulse border border-slate-200/50 dark:border-slate-700/50" />
                <div className="min-h-[550px] w-full rounded-2xl bg-slate-100/50 dark:bg-slate-800/20 animate-pulse border border-slate-200/50 dark:border-slate-700/50" />
            </div>
        )
    }

    return (
        <div className="space-y-6 pb-20 animate-in fade-in duration-500">
                {/* 1. Ranking Table (Main) */}
                <DashboardRankingTable marketData={deferredMarketData} settings={settings} analysis={deferredAnalysis} />

                {/* 2. Live Strike Change Tracker (Moved Below) */}
                <LiveStrikeChangeTracker
                    currentAnalysis={deferredAnalysis}
                    marketData={deferredMarketData}
                    mode={mode}
                    settings={settings}
                />

                {/* 3. Dashboard Analytics Switcher */}
                <DashboardTableSwitcher
                    settings={{ symbol: deferredSymbol, expiry: effectiveExpiry || '', mode: deferredMode, range: deferredRange }}
                    marketData={deferredMarketData}
                    isMarketLoading={isMarketLoading}
                    analysis={deferredAnalysis}
                />
        </div>
    )
}
